import { SelectOption } from '@/shared/types';

import { dictionary } from '../endpoints/dictionary';
import { SetOptions, PaymentServiceResponseOption, OrgNpuOption, StreetSelectOption, DocumentTypeCode } from '../types';

import { handleEmptyResponse } from './utils';

export const getCountrySelect = async (option?: SetOptions): Promise<SelectOption[]> => {
  const countries = handleEmptyResponse(await dictionary.getCountry());
  const newOptions = countries.map<SelectOption>(
    (country) =>
      ({
        id: country.id,
        label: country.shortName,
      }) satisfies SelectOption,
  );

  option && option(newOptions);

  return newOptions;
};

export const getRegionSelect = async (option?: SetOptions): Promise<SelectOption[]> => {
  const regions = handleEmptyResponse(await dictionary.getRegion());
  const newOptions = regions.map<SelectOption>(
    (region) =>
      ({
        id: region.id,
        label: region.name,
      }) satisfies SelectOption,
  );

  option && option(newOptions);

  return newOptions;
};

export const getDistrictSelect = async (id: string, option?: SetOptions): Promise<SelectOption[]> => {
  const districts = id ? handleEmptyResponse(await dictionary.getDistrict(id)) : [];
  const newOptions = districts.map<SelectOption>(
    (district) =>
      ({
        id: district.id,
        label: district.name,
      }) satisfies SelectOption,
  );

  option && option(newOptions);

  return newOptions;
};

export const getCitySelect = async (id: Nullable<string>, option?: SetOptions): Promise<SelectOption[]> => {
  const cities = id ? handleEmptyResponse(await dictionary.getCity(id)) : [];
  const newOptions = cities.map<SelectOption>(
    (city) =>
      ({
        id: city.id,
        label: city.name,
      }) satisfies SelectOption,
  );

  option && option(newOptions);

  return newOptions;
};

export const getStreetSelect = async (
  id: string,
  option?: SetOptions<StreetSelectOption>,
): Promise<StreetSelectOption[]> => {
  const streets = handleEmptyResponse(await dictionary.getStreet(id));
  const newOptions = streets.map((street) => ({
    id: street.id,
    label: (street.subType?.abbr || '') + ' ' + street.name,
    postCode: street.postCode,
  }));

  option && option(newOptions);

  return newOptions;
};

export const getOrgNpuSelect = async (
  option?: SetOptions<SelectOption & { isPayable: boolean }>,
): Promise<OrgNpuOption[]> => {
  const authorities = handleEmptyResponse(await dictionary.getAuthorities());
  const newOptions = authorities.map<SelectOption & { isPayable: boolean }>(
    (authority) =>
      ({
        id: authority.id,
        label: authority.name,
        isPayable: authority.isPayable,
      }) satisfies SelectOption & { isPayable: boolean },
  );

  option && option(newOptions);
  return newOptions;
};

export const getServicesForPaymentSelect = async (option: SetOptions<PaymentServiceResponseOption>): Promise<void> => {
  const services = handleEmptyResponse(await dictionary.getServicesForPayment());

  const newOptions = Object.entries(services).map(([key, data]) => ({
    id: data[0].id,
    label: key,
    data,
  }));

  option(newOptions);
};

export const getIndividualStatusSelect = async (props: {
  option?: SetOptions;
  objectOfPurchase?: string;
}): Promise<Array<SelectOption & { documents: Array<{ required: boolean; code: DocumentTypeCode }> }>> => {
  const individualStatuses = handleEmptyResponse(await dictionary.getIndividualStatus(props.objectOfPurchase));
  const newOptions = individualStatuses.map<
    SelectOption & { documents: Array<{ required: boolean; code: DocumentTypeCode }> }
  >(
    (individualStatus) =>
      ({
        id: individualStatus.id,
        label: individualStatus.name,
        documents: individualStatus.documents,
      }) satisfies SelectOption & { documents: Array<{ required: boolean; code: DocumentTypeCode }> },
  );

  props.option && props.option(newOptions);
  return newOptions;
};

export const getDeliveryRegion = async (option: SetOptions): Promise<SelectOption[]> => {
  const regions = handleEmptyResponse(await dictionary.getDeliveryRegions());

  const newOptions = (regions as Array<{ id: string; name: string }>).map<SelectOption>(
    (district) =>
      ({
        id: district.id,
        label: district.name,
      }) satisfies SelectOption,
  );

  option(newOptions);

  return newOptions;
};

export const getDeliveryDistrict = async (id: string, option: SetOptions): Promise<SelectOption[]> => {
  const districts = id ? handleEmptyResponse(await dictionary.getDeliveryDistricts(id)) : [];

  const newOptions = (districts as Array<{ id: string; name: string }>).map<SelectOption>(
    (district) =>
      ({
        id: district.id,
        label: district.name,
      }) satisfies SelectOption,
  );

  option(newOptions);

  return newOptions;
};

export const getDeliverySettlement = async (id: string, option: SetOptions): Promise<SelectOption[]> => {
  const settlements = id ? handleEmptyResponse(await dictionary.getDeliverySettlements(id)) : [];

  const newOptions = (settlements as Array<{ id: string; name: string }>).map<SelectOption>(
    (district) =>
      ({
        id: district.id,
        label: district.name,
      }) satisfies SelectOption,
  );

  option(newOptions);

  return newOptions;
};

export const getDeliveryWarehouse = async (id: string, option: SetOptions): Promise<SelectOption[]> => {
  const warehouses = id ? handleEmptyResponse(await dictionary.getDeliveryWarehouses(id)) : [];

  const newOptions = warehouses.map<SelectOption>(
    (district) =>
      ({
        id: district.id,
        label: district.address,
      }) satisfies SelectOption,
  );

  option(newOptions);

  return newOptions;
};

export const getDeliveryPostOfficeAddressBySettlement = async (
  id: string,
  option: SetOptions,
): Promise<SelectOption[]> => {
  const warehouses = id ? handleEmptyResponse(await dictionary.getDeliveryPostOfficeBySettlement(id)) : [];

  const newOptions = warehouses.map<SelectOption>(
    (district) =>
      ({
        id: district.zip_code,
        label: district.street,
      }) satisfies SelectOption,
  );

  option(newOptions);

  return newOptions;
};

export const getDeliveryStreetsBySettlement = async (id: string, option: SetOptions): Promise<SelectOption[]> => {
  const warehouses = id ? handleEmptyResponse(await dictionary.getDeliveryStreets(id)) : [];

  const newOptions = warehouses.map<SelectOption>(
    (district) =>
      ({
        id: district.id,
        label: district.name,
      }) satisfies SelectOption,
  );

  option(newOptions);

  return newOptions;
};
